import { Stack } from '@mui/material'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import { RevenueMonthlyAccordions } from '@/features/site/components/revenue/RevenueMonthlyAccordions'
import type { RevenueDataRow, SiteRevenuePerMarketProgram } from '@/features/site/types/revenue'
import { getRevenueDataByYear } from '@/features/site/utils/revenueUtils/formatRevenueData'

type SiteRevenueDataGridProps = {
  revenues: SiteRevenuePerMarketProgram | null
}

export function SiteRevenueDataGrid({ revenues }: Readonly<SiteRevenueDataGridProps>) {
  if (!revenues) {
    return null
  }

  const dataByTimestamp = aggregateRevenueDataByTimestamp(revenues)

  if (dataByTimestamp.length === 0) {
    return null
  }

  const dataByYear = getRevenueDataByYear(dataByTimestamp)

  return (
    <Stack spacing={4}>
      {Object.entries(dataByYear).map(([year, revenueData]) => (
        <Stack key={year} spacing={2}>
          <CustomTypography variant="h2">{year}</CustomTypography>
          <RevenueMonthlyAccordions isLoadingRevenueTimeSeries={false} revenueData={revenueData} />
        </Stack>
      ))}
    </Stack>
  )
}

/**
 * Aggregates revenue data by timestamp from a map of revenue data rows.
 * The function groups the data by timestamp and sums up the `soldCapacity`, `activatedEnergy`, and `revenue` fields.
 * A unique ID is assigned to each aggregated entry, starting from 1 and incrementing with each new entry.
 */
function aggregateRevenueDataByTimestamp(data: Map<string, RevenueDataRow[] | null>) {
  const aggregatedMap = new Map<string, RevenueDataRow>()

  for (const [, revenueDataRows] of data) {
    if (!revenueDataRows) continue

    for (const { timestamp, capacity, soldCapacity, activatedEnergy, revenue, currency, id } of revenueDataRows) {
      const timestampKey = timestamp.toISO() // Use ISO string as the key for the timestamp

      if (!aggregatedMap.has(timestampKey!)) {
        aggregatedMap.set(timestampKey!, {
          id,
          timestamp,
          capacity: 0,
          soldCapacity: 0,
          activatedEnergy: 0,
          revenue: 0,
          currency,
        })
      }

      const entry = aggregatedMap.get(timestampKey!) as RevenueDataRow
      entry.capacity += capacity
      entry.soldCapacity += soldCapacity
      entry.activatedEnergy += activatedEnergy
      entry.revenue += revenue
    }
  }

  return Array.from(aggregatedMap.values())
}
